import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Briefcase, 
  ClipboardList, 
  CheckCircle2, 
  User,
  MessageSquare,
  AlertCircle,
  Clock,
  Star,
  Settings,
  RefreshCw,
  ArrowRight,
  Hammer,
  LogOut,
  Home
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const ContractorDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contractors/dashboard`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
        
        // If profile not complete, redirect to onboarding
        if (!data.profile_complete) {
          navigate('/contractor/account');
          return;
        }
      } else if (response.status === 404) {
        // Profile doesn't exist, redirect to account setup
        navigate('/contractor/account');
      } else {
        toast.error('Failed to load dashboard');
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchRecentJobs = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/contractors/jobs?status=active&limit=5`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecentJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Error fetching recent jobs:', error);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchRecentJobs();
  }, [fetchDashboard, fetchRecentJobs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Unable to load dashboard</h2>
        <Button onClick={fetchDashboard}>Retry</Button>
      </div>
    );
  }

  const tiles = [
    {
      title: 'Job Offers',
      count: dashboard.counts?.offers || 0,
      icon: Briefcase,
      color: 'bg-amber-500',
      bgColor: 'bg-amber-50',
      link: '/contractor/job-offers',
      description: 'New jobs available',
    },
    {
      title: 'Active Jobs',
      count: dashboard.counts?.active || 0,
      icon: Clock,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      link: '/contractor/active-jobs',
      description: 'Accepted & scheduled',
    },
    {
      title: 'In Progress',
      count: dashboard.counts?.in_progress || 0,
      icon: Hammer,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      link: '/contractor/active-jobs',
      description: 'Currently working on',
    },
    {
      title: 'Completed',
      count: dashboard.counts?.completed || 0,
      icon: CheckCircle2,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      link: '/contractor/completed-jobs',
      description: 'Finished jobs',
    },
    {
      title: 'Messages',
      count: dashboard.unread_messages || 0,
      icon: MessageSquare,
      color: 'bg-indigo-500',
      bgColor: 'bg-indigo-50',
      link: '/contractor/active-jobs',
      description: 'Unread messages',
      badge: dashboard.unread_messages > 0 ? 'New' : null,
    },
    {
      title: 'My Account',
      count: null,
      icon: User,
      color: 'bg-slate-500',
      bgColor: 'bg-slate-50',
      link: '/contractor/account',
      description: 'Profile & settings',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="contractor-dashboard">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-slate-200 fixed top-0 left-0 right-0 z-50 px-4 md:px-8 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <div className="p-1.5 bg-blue-600 rounded-lg">
              <Home className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-blue-600">LetWise</h1>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/contractor/account">
              <Button variant="ghost" size="sm">
                <User className="h-4 w-4 mr-2" />
                Account
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="contractor-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Header - with top margin to account for fixed navbar */}
      <div className="mb-8 mt-16">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Contractor Portal</h1>
            <p className="text-slate-600 mt-1">Welcome back! Here&apos;s your overview.</p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => { fetchDashboard(); fetchRecentJobs(); }}
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Alerts */}
        {dashboard.profile_alerts && dashboard.profile_alerts.length > 0 && (
          <div className="mt-4 space-y-2">
            {dashboard.profile_alerts.map((alert, idx) => (
              <div key={idx} className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{alert}</span>
              </div>
            ))}
          </div>
        )}

        {/* Stats Bar */}
        {dashboard.stats && (
          <div className="mt-4 flex flex-wrap gap-4">
            <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-2 border">
              <Star className="h-4 w-4 text-yellow-500" />
              <span className="text-sm font-medium">
                {dashboard.stats.average_rating?.toFixed(1) || '0.0'} Rating
              </span>
              <span className="text-xs text-slate-500">
                ({dashboard.stats.total_reviews || 0} reviews)
              </span>
            </div>
            <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-2 border">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">
                {dashboard.stats.total_jobs_completed || 0} Jobs Completed
              </span>
            </div>
            {!dashboard.verified && (
              <Badge variant="outline" className="border-amber-300 text-amber-700">
                Pending Verification
              </Badge>
            )}
            {dashboard.verified && (
              <Badge className="bg-green-100 text-green-700 border-green-200">
                Verified Contractor
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Tiles Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {tiles.map((tile) => (
          <Link 
            key={tile.title} 
            to={tile.link}
            className="block"
            data-testid={`tile-${tile.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Card className={`h-full hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-blue-300 ${tile.bgColor}`}>
              <CardContent className="p-4 text-center">
                <div className={`inline-flex p-3 rounded-full ${tile.color} text-white mb-3`}>
                  <tile.icon className="h-6 w-6" />
                </div>
                {tile.count !== null && (
                  <p className="text-3xl font-bold text-slate-900">{tile.count}</p>
                )}
                <p className="font-semibold text-slate-700">{tile.title}</p>
                <p className="text-xs text-slate-500 mt-1">{tile.description}</p>
                {tile.badge && (
                  <Badge className="mt-2 bg-red-500 text-white">{tile.badge}</Badge>
                )}
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent Active Jobs */}
      {recentJobs.length > 0 && (
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recent Active Jobs</CardTitle>
                <CardDescription>Your current work</CardDescription>
              </div>
              <Link to="/contractor/active-jobs">
                <Button variant="ghost" size="sm">
                  View All <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentJobs.map((job) => (
                <Link 
                  key={job.id} 
                  to={`/contractor/jobs/${job.id}`}
                  className="block p-4 rounded-lg border hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{job.title || job.description?.slice(0, 50)}</p>
                      <p className="text-sm text-slate-500">{job.property_address}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant={job.status === 'in_progress' ? 'default' : 'outline'}>
                        {job.status?.replace('_', ' ')}
                      </Badge>
                      {job.scheduled_date && (
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(job.scheduled_date).toLocaleDateString('en-GB')}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Link to="/contractor/job-offers">
              <Button data-testid="view-offers-btn">
                <Briefcase className="h-4 w-4 mr-2" />
                View Job Offers ({dashboard.counts?.offers || 0})
              </Button>
            </Link>
            <Link to="/contractor/account">
              <Button variant="outline">
                <Settings className="h-4 w-4 mr-2" />
                Update Profile
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContractorDashboard;
